import { bundle } from '@scalar/json-magic/bundle'
import { fetchUrls } from '@scalar/json-magic/bundle/plugins/browser'
import type { OpenAPIV3_1 } from '@scalar/openapi-types'
import type { ApiReferenceConfiguration } from '@scalar/types/api-reference'
import type { WorkspaceStore } from '@scalar/workspace-store/client'
import { n } from 'neverpanic'
import type { Ref } from 'vue'

import type { Api } from '@/api'
import { createError } from '@/entities'
import type { ApiMetadata } from '@/entities/registry/document'
import { restoreAuthSecretsFromStorage } from '@/helpers'
import { createDocumentName } from '@/registry/create-document-name'

export const loadDocument = n.safeFn(
  async ({
    namespace,
    slug,
    workspaceStore,
    registryDocuments,
    getAccessToken,
    registryUrl,
    config,
    api,
    removable,
  }: {
    namespace: string
    slug: string
    workspaceStore: WorkspaceStore
    getAccessToken?: () => string
    registryDocuments: Ref<ApiMetadata[]>
    registryUrl: string
    config: Partial<ApiReferenceConfiguration>
    api: Api
    removable?: boolean
  }) => {
    const getDocumentResult = await api.getDocument({
      namespace,
      slug,
    })

    if (!getDocumentResult.success) {
      return getDocumentResult
    }

    registryDocuments.value.push({ ...getDocumentResult.data, removable })

    const url = new URL(`/@${namespace}/apis/${slug}/latest`, registryUrl)

    const headers: {
      headers: HeadersInit
      domains: string[]
    }[] = []

    const token = getAccessToken?.()

    if (token) {
      headers.push({
        domains: [new URL(registryUrl).host],
        headers: {
          'x-scalar-auth': token,
        },
      })
    }

    const document: OpenAPIV3_1.Document = await bundle(url.toString(), {
      plugins: [
        fetchUrls({
          headers,
        }),
      ],
      treeShake: false,
    })

    const documentName = createDocumentName(namespace, slug)

    await workspaceStore.addDocument(
      {
        name: documentName,
        document,
      },
      config,
    )

    workspaceStore.update('x-scalar-active-document', documentName)

    restoreAuthSecretsFromStorage({ documentName, workspaceStore })

    return {
      success: true,
      data: getDocumentResult.data,
    }
  },
  (originalError) => createError('UNABLE_TO_LOAD_DOCUMENT', originalError),
)
